import { FiveInfoWithId, Languages } from '@livv/models';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Container,
    Divider,
    Typography,
} from '@mui/material';
import {
    QueryConstraint,
    QueryDocumentSnapshot,
    getDocs,
    limit,
    orderBy,
    query,
    startAfter,
    where,
} from 'firebase/firestore';
import { NextPage } from 'next';
import Head from 'next/head';
import useTranslation from 'next-translate/useTranslation';
import { useCallback, useEffect, useState } from 'react';
import DisplayFullText from '@components/FullText/DisplayFullText';
import LoadMoreButton from '@components/LoadMoreButton';
import VogelActuHeader from '@components/VogelActu/VogelActuHeader';
import { MARGIN_BEFORE_FOOTER } from '@styles/consts';
import { fiveInfosCollection } from '@utils/collections';
import { getDate } from '@utils/date';

const PAGE_SIZE = 3;
const FETCH_LIMIT = PAGE_SIZE + 1;

const protectedTextSx = {
    MozUserSelect: 'none',
    WebkitUserSelect: 'none',
    msUserSelect: 'none',
    userSelect: 'none',
};

interface FiveInfosPageData {
    hasMore: boolean;
    items: FiveInfoWithId[];
    lastDocument: QueryDocumentSnapshot<FiveInfoWithId> | null;
}

const VogelActuFiveInfosPage: NextPage = () => {
    const { t, lang } = useTranslation('vogelActuFiveInfos');
    const [fiveInfos, setFiveInfos] = useState<FiveInfoWithId[]>([]);
    const [isInitialLoading, setIsInitialLoading] = useState(true);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [hasMore, setHasMore] = useState(false);
    const [lastDocument, setLastDocument] = useState<QueryDocumentSnapshot<FiveInfoWithId> | null>(
        null,
    );
    const [hasError, setHasError] = useState(false);
    const [refreshAttempt, setRefreshAttempt] = useState(0);

    const fetchFiveInfos = useCallback(
        async (cursor?: QueryDocumentSnapshot<FiveInfoWithId> | null) => {
            const constraints: QueryConstraint[] = [
                where('isPublished', '==', true),
                orderBy('publishedAt', 'desc'),
                limit(FETCH_LIMIT),
            ];
            if (cursor) {
                constraints.push(startAfter(cursor));
            }

            const fiveInfosQuery = query(fiveInfosCollection, ...constraints);
            const snapshot = await getDocs(fiveInfosQuery);
            const docs = snapshot.docs as QueryDocumentSnapshot<FiveInfoWithId>[];
            const pageDocs = docs.slice(0, PAGE_SIZE);

            return {
                hasMore: docs.length > PAGE_SIZE,
                items: pageDocs.map((docSnapshot) => docSnapshot.data()),
                lastDocument: pageDocs.length ? pageDocs[pageDocs.length - 1] : null,
            } satisfies FiveInfosPageData;
        },
        [],
    );

    useEffect(() => {
        let isMounted = true;

        const loadFiveInfos = async () => {
            setIsInitialLoading(true);
            setHasError(false);

            try {
                const {
                    hasMore: hasNextPage,
                    items,
                    lastDocument: nextCursor,
                } = await fetchFiveInfos();

                if (!isMounted) {
                    return;
                }

                setFiveInfos(items);
                setHasMore(hasNextPage);
                setLastDocument(nextCursor);
            } catch (error) {
                console.error(error);
                if (!isMounted) {
                    return;
                }
                setHasError(true);
            } finally {
                if (isMounted) {
                    setIsInitialLoading(false);
                }
            }
        };

        loadFiveInfos().catch(() => undefined);

        return () => {
            isMounted = false;
        };
    }, [fetchFiveInfos, refreshAttempt]);

    const handleLoadMore = useCallback(async () => {
        if (!hasMore || isLoadingMore) {
            return;
        }

        setIsLoadingMore(true);
        try {
            const {
                hasMore: hasNextPage,
                items,
                lastDocument: nextCursor,
            } = await fetchFiveInfos(lastDocument);
            setFiveInfos((previousFiveInfos) => [...previousFiveInfos, ...items]);
            setHasMore(hasNextPage);
            setLastDocument(nextCursor);
        } catch (error) {
            console.error(error);
        } finally {
            setIsLoadingMore(false);
        }
    }, [fetchFiveInfos, hasMore, isLoadingMore, lastDocument]);

    return (
        <>
            <VogelActuHeader subtitle={t('subtitle')} />
            <Head>
                <title>{t('pageTitle')}</title>
                <meta content={t('meta.description')} name="description" />
                <meta content="noindex" name="robots" />
            </Head>
            <Container maxWidth="md" sx={{ mb: MARGIN_BEFORE_FOOTER, mt: 4 }}>
                {isInitialLoading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                        <CircularProgress />
                    </Box>
                )}
                {!isInitialLoading && hasError && (
                    <Alert
                        action={
                            <Button
                                color="inherit"
                                onClick={() => setRefreshAttempt((count) => count + 1)}
                                size="small"
                            >
                                {t('retry')}
                            </Button>
                        }
                        severity="error"
                        sx={{ mb: 3 }}
                    >
                        {t('loadError')}
                    </Alert>
                )}
                {!isInitialLoading && !hasError && fiveInfos.length === 0 && (
                    <Typography
                        color="grey.300"
                        sx={{ py: 6, textAlign: 'center' }}
                        variant="body1"
                    >
                        {t('emptyState')}
                    </Typography>
                )}
                {!isInitialLoading &&
                    !hasError &&
                    fiveInfos.map((fiveInfo, index) => {
                        const publishedDate = getDate(
                            fiveInfo.publishedAt.toDate(),
                            lang as Languages,
                        );
                        const displayedContent =
                            lang === Languages.en && fiveInfo.contentEn?.trim().length
                                ? fiveInfo.contentEn
                                : fiveInfo.content;

                        return (
                            <Box key={fiveInfo.id} sx={{ mb: 5 }}>
                                <Typography
                                    color="primary"
                                    sx={{ mb: 2, mt: 7 }}
                                    textAlign="center"
                                    variant="h2"
                                >
                                    {publishedDate}
                                </Typography>
                                <DisplayFullText
                                    contentSx={protectedTextSx}
                                    sanitizeOptions={{ insertLawLexTags: false }}
                                    unsanitizedContent={displayedContent}
                                />
                                {index < fiveInfos.length - 1 && (
                                    <Divider sx={{ mb: 10, mt: 10 }} />
                                )}
                            </Box>
                        );
                    })}
                {!isInitialLoading && !hasError && hasMore && (
                    <LoadMoreButton loading={isLoadingMore} onClick={handleLoadMore} sx={{ mt: 3 }}>
                        {t('showMore')}
                    </LoadMoreButton>
                )}
            </Container>
        </>
    );
};

export default VogelActuFiveInfosPage;
